import React, { memo } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import classNames from 'classnames';
import { useSelector } from 'react-redux';

import {
  getCanBeAccepted,
  getCanBeDonated,
  getCanBeExchanged,
  getCanExchangeAndDonate,
  getCanExchangeOrDonate,
  getReverseReason,
} from '../../../store/data/data.selectors';
import { IAlyceCustomTheme } from '../../../../../styles/alyceTheme';
import { IProduct } from '../../../types/Product.types';

import AcceptButton from './AcceptButton';
import ExchangeButton from './ExchangeButton';
import DonateButton from './DonateButton';
import ButtonsDivider from './ButtonsDivider';
import FlowWrapper from './FlowWrapper';

const useStyles = makeStyles<IAlyceCustomTheme>(({ palette }) => ({
  outOfStock: {
    color: palette.grey.main,
  },
}));

export interface IGiftCountryFlowProps {
  onAccept: (product: IProduct, confirmedFlowStepId: string | null) => void;
  onExchange: (useDonation: boolean) => void;
  onDonate: () => void;
}

const GiftCountryFlow = ({ onAccept, onExchange, onDonate }: IGiftCountryFlowProps): JSX.Element => {
  const classes = useStyles();

  const canBeAccepted = useSelector(getCanBeAccepted);
  const canBeExchanged = useSelector(getCanBeExchanged);
  const canExchangeOrDonate = useSelector(getCanExchangeOrDonate);
  const reverseReason = useSelector(getReverseReason);
  const canBeDonated = useSelector(getCanBeDonated);
  const canExchangeAndDonate = useSelector(getCanExchangeAndDonate);
  const isDividerVisible = canBeAccepted && canExchangeOrDonate;

  return (
    <FlowWrapper>
      {canBeAccepted && (
        <Grid container direction="row">
          <Grid item xs={12}>
            <AcceptButton onSubmit={onAccept} />
          </Grid>
        </Grid>
      )}
      {isDividerVisible && <ButtonsDivider />}
      {!canBeAccepted && (
        <Box mb={2}>
          <Typography className={classNames('Body-Regular-Center-Inactive', classes.outOfStock)}>
            {reverseReason}
          </Typography>
        </Box>
      )}
      {canExchangeOrDonate && (
        <Grid container direction="row" spacing={4}>
          {canBeExchanged && (
            <Grid item xs={12} sm={canExchangeAndDonate ? 6 : 12}>
              <ExchangeButton onSubmit={onExchange} />
            </Grid>
          )}
          {canBeDonated && (
            <Grid item xs={12} sm={canExchangeAndDonate ? 6 : 12}>
              <DonateButton onSubmit={onDonate} />
            </Grid>
          )}
        </Grid>
      )}
    </FlowWrapper>
  );
};

export default memo<IGiftCountryFlowProps>(GiftCountryFlow);
